import React, { useState } from "react";
import {
  Box,
  Button,
  ButtonGroup,
  FormControl,
  FormLabel,
  Grid,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Wrap,
  WrapItem,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import { FiCalendar, FiChevronLeft, FiChevronRight, FiX } from "react-icons/fi";
import {
  DATE_PRESETS,
  EMPTY_RANGE,
  dateRangeLabel,
  isRangeSet,
  monthGrid,
  parseDayKey,
  pickDay,
  toDayKey,
} from "../../utils/dateRange";

// The "Date" button on the SMS Status number tabs and the calendar modal it
// opens. `fieldLabel` says which date the range applies to ("last failure").

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const Calendar = ({ month, onMonth, draft, onPick, today }) => {
  const [hover, setHover] = useState("");
  const muted = useColorModeValue("gray.500", "gray.400");
  const inRangeBg = useColorModeValue("teal.50", "whiteAlpha.200");
  const hoverBg = useColorModeValue("gray.100", "whiteAlpha.100");
  // While only the start is picked, preview the range up to the hovered day.
  const end = draft.to || (draft.from && hover ? hover : "");
  const [lo, hi] = draft.from && end && end < draft.from ? [end, draft.from] : [draft.from, end];

  return (
    <Box>
      <HStack justify="space-between" mb={2}>
        <IconButton
          size="sm"
          variant="ghost"
          aria-label="Previous month"
          icon={<FiChevronLeft />}
          onClick={() => onMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
        />
        <Text fontWeight="semibold" data-testid="calendar-month">
          {month.toLocaleDateString("en-US", { month: "long", year: "numeric" })}
        </Text>
        <IconButton
          size="sm"
          variant="ghost"
          aria-label="Next month"
          icon={<FiChevronRight />}
          onClick={() => onMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
        />
      </HStack>
      <Grid templateColumns="repeat(7, 1fr)" gap={1} onMouseLeave={() => setHover("")}>
        {WEEKDAYS.map((d) => (
          <Text key={d} fontSize="xs" color={muted} textAlign="center" py={1}>
            {d}
          </Text>
        ))}
        {monthGrid(month.getFullYear(), month.getMonth())
          .flat()
          .map((key, i) => {
            if (!key) return <Box key={`pad-${i}`} />;
            const isEdge = key === lo || key === hi;
            const inside = lo && hi && key > lo && key < hi;
            return (
              <Button
                key={key}
                size="sm"
                h={9}
                px={0}
                fontWeight={key === today ? "bold" : "normal"}
                variant={isEdge ? "solid" : "ghost"}
                colorScheme={isEdge ? "teal" : "gray"}
                bg={isEdge ? undefined : inside ? inRangeBg : undefined}
                borderWidth={key === today && !isEdge ? "1px" : 0}
                borderColor="teal.400"
                _hover={isEdge ? undefined : { bg: hoverBg }}
                onMouseEnter={() => setHover(key)}
                onClick={() => onPick(key)}
                aria-label={parseDayKey(key).toDateString()}
                aria-pressed={isEdge || Boolean(inside)}
              >
                {Number(key.slice(8))}
              </Button>
            );
          })}
      </Grid>
    </Box>
  );
};

const DateRangeFilter = ({ value = EMPTY_RANGE, onChange, fieldLabel = "date" }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [draft, setDraft] = useState(value);
  const [month, setMonth] = useState(() => new Date());
  const active = isRangeSet(value);
  const muted = useColorModeValue("gray.600", "gray.400");
  const now = new Date();
  const today = toDayKey(now);

  const open = () => {
    setDraft(value);
    const shown = parseDayKey(value.to || value.from) || now;
    setMonth(new Date(shown.getFullYear(), shown.getMonth(), 1));
    onOpen();
  };

  const apply = (range) => {
    // A lone start date means just that day.
    onChange(range.from && !range.to ? { from: range.from, to: range.from } : range);
    onClose();
  };

  const typed = (field) => (e) => {
    const next = { ...draft, [field]: e.target.value };
    if (next.from && next.to && next.to < next.from) next[field === "from" ? "to" : "from"] = e.target.value;
    setDraft(next);
    const shown = parseDayKey(e.target.value);
    if (shown) setMonth(new Date(shown.getFullYear(), shown.getMonth(), 1));
  };

  return (
    <>
      <ButtonGroup size="sm" isAttached variant="outline" flexShrink={0}>
        <Button
          leftIcon={<FiCalendar />}
          onClick={open}
          colorScheme={active ? "teal" : "gray"}
          aria-label={`Filter by ${fieldLabel}`}
          data-testid="date-filter"
        >
          {dateRangeLabel(value)}
        </Button>
        {active && (
          <IconButton
            colorScheme="teal"
            aria-label="Clear date filter"
            icon={<FiX />}
            onClick={() => onChange(EMPTY_RANGE)}
          />
        )}
      </ButtonGroup>

      <Modal isOpen={isOpen} onClose={onClose} size="sm" isCentered>
        <ModalOverlay />
        <ModalContent data-testid="date-filter-modal">
          <ModalHeader pb={1}>Filter by date</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="xs" color={muted} mb={3}>
              Shows numbers whose {fieldLabel} falls in this range.
            </Text>
            <Wrap spacing={1.5} mb={4}>
              {DATE_PRESETS.map((p) => (
                <WrapItem key={p.key}>
                  <Button size="xs" borderRadius="full" variant="outline" onClick={() => apply(p.range(now))}>
                    {p.label}
                  </Button>
                </WrapItem>
              ))}
            </Wrap>
            <Calendar
              month={month}
              onMonth={setMonth}
              draft={draft}
              onPick={(key) => setDraft(pickDay(draft, key))}
              today={today}
            />
            <HStack spacing={2} mt={4}>
              <FormControl>
                <FormLabel fontSize="xs" mb={1}>
                  From
                </FormLabel>
                <Input size="sm" type="date" value={draft.from} max={draft.to || undefined} onChange={typed("from")} />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xs" mb={1}>
                  To
                </FormLabel>
                <Input size="sm" type="date" value={draft.to} min={draft.from || undefined} onChange={typed("to")} />
              </FormControl>
            </HStack>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button size="sm" variant="ghost" mr="auto" onClick={() => apply(EMPTY_RANGE)}>
              Any date
            </Button>
            <Button size="sm" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button size="sm" colorScheme="teal" onClick={() => apply(draft)}>
              Apply
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default DateRangeFilter;
